












































































import {
	computed,
	defineComponent,
	PropType,
	ref,
} from '@vue/composition-api';
import { v } from '@front-app/components/form/validation/useValidation';
import { required } from '@front-app/components/form/validation/validators';
import BaseEditor from '@front-app/modules/group/highlights/components/BaseEditor.vue';
import BasicTextEditor from '@front-app/components/inputs/BasicTextEditor.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import HelpTooltipBlock from '@front-app/components/dropdowns/HelpTooltipBlock.vue';
import HrefInputSection from '@front-app/modules/group/highlights/components/Button/editorSections/HrefInputSection.vue';
import SelectBox from '@front-app/components/inputs/SelectBox.vue';
import { extractSurveyIdFromUrl, extractIdFromSurveyEmbedCode, convertSurveyIdToUrl } from '@front-app/util/content';
import { HighlightsSurveyComponent, SurveyProvider } from '@front-app/modules/group/highlights/model';
import { syncedRef } from '@front-app/util/reactivity';
import { useHighlightsInput } from '@front-app/modules/group/highlights/hooks/useHighlightsInput';
import { useTranslate } from '@front-app/hooks/useTranslate';

const OPTIONS: SurveyProvider[] = [
	SurveyProvider.JOTFORM,
	SurveyProvider.SURVEY_MONKEY,
];

const LABELS: Record<SurveyProvider, string> = {
	[SurveyProvider.JOTFORM]: 'survey-types.jotform',
	[SurveyProvider.SURVEY_MONKEY]: 'survey-types.survey-monkey',
};

export default defineComponent({
	components: {
		BaseEditor,
		BasicTextEditor,
		FormLabel,
		FormError,
		FormSection,
		HelpTooltipBlock,
		HrefInputSection,
		SelectBox,
	},
	props: {
		value: {
			type: Object as PropType<HighlightsSurveyComponent>,
			required: true,
		},
	},
	setup(props, ctx) {
		// Survey type
		const $t = useTranslate(ctx);
		const getLabelForKey = (key: SurveyProvider): string => $t(LABELS[key]) as unknown as string;
		const selectOptions = computed(() => OPTIONS.map((key) => ({
			key,
			label: getLabelForKey(key),
		})));
		const surveyType = computed(() => {
			if (props.value.meta.type) {
				return {
					key: props.value.meta.type,
					label: getLabelForKey(props.value.meta.type),
				};
			}
			return {
				key: SurveyProvider.JOTFORM,
				label: getLabelForKey(SurveyProvider.JOTFORM),
			};
		});

		// Survey URL and embed code
		const surveyUrl = syncedRef(() => {
			// Fill in the provided source URL or construct one from id and survey provider type
			if (props.value.meta.src) {
				return props.value.meta.src;
			}
			if (props.value.meta.surveyId && props.value.meta.type) {
				return convertSurveyIdToUrl(props.value.meta.surveyId, props.value.meta.type);
			}
			return '';
		}).ref;
		const embedCode = ref('');

		const { doMetaInput } = useHighlightsInput(ctx, computed(() => props.value));

		const handleUrlInput = async (url: string) => {
			const surveyId = extractSurveyIdFromUrl(url);

			if (surveyId) {
				doMetaInput({
					src: url,
					surveyId,
					type: surveyType.value.key,
				});

				if (embedCode.value) {
					embedCode.value = '';
				}
			} else {
				surveyUrl.value = url;
			}
		};

		const handleEmbedInput = async (newEmbedCode: string) => {
			const surveyId = extractIdFromSurveyEmbedCode(newEmbedCode, surveyType.value.key);

			if (surveyId) {
				doMetaInput({
					src: '',
					surveyId,
					type: surveyType.value.key,
				});

				if (surveyUrl.value) {
					surveyUrl.value = '';
				}
			} else {
				embedCode.value = newEmbedCode;
			}
		};

		// Validation
		const validation = v({
			type: {
				value: computed(() => props.value.meta.type),
				rules: [required()],
			},
			surveyId: {
				value: computed(() => props.value.meta.surveyId),
				rules: [required()],
			},
		});

		return {
			// Survey type
			surveyType,
			getLabelForKey,
			selectOptions,
			// Survey URL and embed code
			surveyUrl,
			handleUrlInput,
			embedCode,
			handleEmbedInput,
			doMetaInput,
			validation,
		};
	},
});
